import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { kakoAPI, packagesAPI, ordersAPI, configAPI, tasksAPI, rankingAPI, advertisementsAPI, applicationsAPI, paymentAdAPI, megaPrizeAPI, getBackendURL, paymentMethodsAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { AlertCircle, CheckCircle2, Loader2, Trophy, Target, ShoppingCart, Crown, Swords, Gift, Smartphone } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import ClientTasks from '../components/customer/ClientTasks';
import ClientRanking from '../components/customer/ClientRanking';
import ClientMegaPrize from '../components/customer/ClientMegaPrize';
import ClientHostEvent from '../components/customer/ClientHostEvent';
import ClientPKBattle from '../components/customer/ClientPKBattle';
import AdvertisementCarousel from '../components/AdvertisementCarousel';
import RecentPurchasesScroll from '../components/RecentPurchasesScroll';
import LiveHostsBar from '../components/LiveHostsBar';
import AudioPlayer from '../components/AudioPlayer';
import SupportButton from '../components/SupportButton';
import WinnersBanner from '../components/home/WinnersBanner';
import CheckoutDialog from '../components/home/CheckoutDialog';
import { fetchFiscalLabels, docInfo, docValid } from '../lib/fiscalDocs';
import { splitWhatsapp } from '../lib/countryDials';
import OrderPaymentView from '../components/home/OrderPaymentView';
import PackagesGrid from '../components/home/PackagesGrid';
import TopAccessBar from '../components/home/TopAccessBar';
import AccountBlockedNotice from '../components/home/AccountBlockedNotice';
import EventBannerCarousel from '../components/home/EventBannerCarousel';
import ThemeBackground from '../components/ThemeBackground';
import ThemeParticles from '../components/ThemeParticles';
import FramedAvatar from '../components/FramedAvatar';

const HomePage = () => {
  const [kakoId, setKakoId] = useState(() => {
    // Carrega ID salvo se existir
    return localStorage.getItem('saved_kako_id') || '';
  });
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('saved_kako_id') ? true : false;
  });
  const [userInfo, setUserInfo] = useState(null);
  const [customerAccount, setCustomerAccount] = useState(null); // conta do cliente logado
  // Conta bloqueada por contestação de pagamento (mensagem + suporte)
  const [blockedMsg, setBlockedMsg] = useState('');
  const [barBottom, setBarBottom] = useState(100); // fim das barras fixas do topo
  // true enquanto o /me do cliente logado não respondeu (evita piscar botão de visitante)
  const [authPending, setAuthPending] = useState(
    () => typeof window !== 'undefined' && !!localStorage.getItem('customer_token')
  );
  const [swapMode, setSwapMode] = useState(false); // trocar destinatário (logado)
  const [swapDraft, setSwapDraft] = useState('');
  const [swapError, setSwapError] = useState('');
  const [siteTheme, setSiteTheme] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // NOVO - Controle de abas visitadas com data (localStorage) + ref para atualização síncrona
  const visitedTabsRef = useRef({});
  
  const [visitedTabs, setVisitedTabs] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    const saved = localStorage.getItem('visited_event_tabs');
    
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.date !== today) {
          localStorage.removeItem('visited_event_tabs');
          visitedTabsRef.current = {};
          return {};
        }
        visitedTabsRef.current = data.tabs || {};
        return data.tabs || {};
      } catch {
        visitedTabsRef.current = {};
        return {};
      }
    }
    visitedTabsRef.current = {};
    return {};
  });
  
  const markTabAsVisited = (tabName) => {
    // Atualiza ref INSTANTANEAMENTE (síncrono)
    visitedTabsRef.current = { ...visitedTabsRef.current, [tabName]: true };
    
    const today = new Date().toISOString().split('T')[0];
    
    // Atualiza estado
    setVisitedTabs(visitedTabsRef.current);
    
    // Salva localStorage
    localStorage.setItem('visited_event_tabs', JSON.stringify({
      date: today,
      tabs: visitedTabsRef.current
    }));
    
    // Força re-render imediato
    setTimeout(() => {
      setVisitedTabs({...visitedTabsRef.current});
    }, 0);
    
    console.log(`✅ ${tabName} desativado - ref:`, visitedTabsRef.current);
  };
  
  const [customerData, setCustomerData] = useState(() => {
    // Carrega dados salvos
    const saved = localStorage.getItem('saved_customer_data');
    if (saved) {
      const dados = JSON.parse(saved);
      // dados antigos do navegador (antes do seletor de país): força escolher país de novo
      if (dados.is_foreigner && !dados.country) return { ...dados, country: '', document: '' };
      return dados;
    }
    return { name: '', cpf: '', whatsapp: '', whatsapp_country: '+55', is_foreigner: false, country: '', document: '', email: '' };
  });
  const [rememberCustomerData, setRememberCustomerData] = useState(() => {
    return localStorage.getItem('saved_customer_data') ? true : true; // Sempre true por padrão
  });
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false); // NOVO - criando pedido
  const [processingPayment, setProcessingPayment] = useState(false); // Spinner fullscreen
  const [unavailableError, setUnavailableError] = useState(null); // Modal de pacote indisponível
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskProgress, setTaskProgress] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [orderStatus, setOrderStatus] = useState(null); // Status do pedido
  const [timeRemaining, setTimeRemaining] = useState(600); // Inicial: 10 minutos (ajustado dinamicamente)
  const [hasPendingPrizes, setHasPendingPrizes] = useState(false);
  const [countInRanking, setCountInRanking] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('pix'); // NOVO - método de pagamento
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState({ pix: true, mbway: false, mbwayMinCoins: 100000 });
  const [eurPrices, setEurPrices] = useState(null);
  const [paymentNotice, setPaymentNotice] = useState(null);
  const [fiscalLabels, setFiscalLabels] = useState(null);
  const [advertisements, setAdvertisements] = useState([]);
  const [showAdmBadge, setShowAdmBadge] = useState(false);
  const [showSellerBadge, setShowSellerBadge] = useState(false);
  const [showWinnersBanner, setShowWinnersBanner] = useState(false); // Novo
  const [winners, setWinners] = useState({ agentes: [], hosts: [] }); // Novo
  const [welcomeClosed, setWelcomeClosed] = useState(false); // Controla se já fechou bem-vindo
  const [previousDayWinners, setPreviousDayWinners] = useState([]); // Ganhadores do dia anterior para scroll
  const [paymentConfirmationAd, setPaymentConfirmationAd] = useState(null); // NOVO - anúncio de confirmação
  const [megaPrizeConfig, setMegaPrizeConfig] = useState(null); // Config do MEGA PRÊMIO
  const [hostEventConfig, setHostEventConfig] = useState(null); // NOVO - Config Meu Host Preferido
  const [pkBattleConfig, setPKBattleConfig] = useState(null); // NOVO - Config Batalha de PK
  const [hostWorkEventConfig, setHostWorkEventConfig] = useState(null); // NOVO - Config Trabalhou Ganhou

  // Mostra banner TOP 3 apenas APÓS fechar bem-vindo (se playlist ativa) OU imediatamente (se playlist inativa)
  useEffect(() => {
    console.log('🎯 Banner check:', {
      show_winners_banner: config?.show_winners_banner,
      agentes: winners.agentes.length,
      hosts: winners.hosts.length,
      playlist_active: config?.playlist_active,
      welcomeClosed
    });
    
    if (!config?.show_winners_banner) {
      console.log('❌ Banner desativado no config');
      return;
    }
    
    const hasWinners = winners.agentes.length > 0 || winners.hosts.length > 0;
    if (!hasWinners) {
      console.log('❌ Sem ganhadores para exibir');
      return;
    }
    
    // Se playlist DESATIVADA: mostra banner imediatamente
    if (!config.playlist_active) {
      console.log('✅ Playlist inativa - mostrando banner em 1s');
      setTimeout(() => {
        setShowWinnersBanner(true);
      }, 1000);
      return;
    }
    
    // Se playlist ATIVA: mostra banner apenas após fechar bem-vindo
    if (welcomeClosed) {
      console.log('✅ Welcome fechado - mostrando banner');
      setShowWinnersBanner(true);
    }
  }, [welcomeClosed, config, winners]);

  // Cronômetro regressivo - calcula tempo real para não parar quando usuário sai da página
  useEffect(() => {
    if (!order || order.status !== 'pending') return;
    
    // PIX e MB WAY expiram em 10 minutos
    const timeoutMinutes = 10;
    const timeoutMs = timeoutMinutes * 60 * 1000;
    
    const orderCreatedAt = new Date(order.created_at);
    const expirationTime = orderCreatedAt.getTime() + timeoutMs;
    
    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(Math.floor((expirationTime - now) / 1000), 0);
      setTimeRemaining(remaining);
      
      if (remaining <= 0) {
        return false; // Para o timer
      }
      return true; // Continua o timer
    };
    
    // Atualiza imediatamente
    if (!updateTimer()) return;
    
    // Atualiza a cada segundo
    const timer = setInterval(() => {
      if (!updateTimer()) {
        clearInterval(timer);
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [order]);

  // Polling para verificar status do pedido
  useEffect(() => {
    if (!order || !order.id) return;
    
    // Para polling se já completo
    if (order.status === 'completed' || orderStatus === 'completed') {
      console.log('⏹️ Polling parado - pedido já completo');
      return;
    }
    
    const checkOrderStatus = async () => {
      try {
        const response = await ordersAPI.get(order.id);
        const updatedOrder = response.data;
        
        console.log('🔄 Polling verificou pedido:', updatedOrder.status, 'Ticket:', updatedOrder.mega_prize_ticket);
        
        // Atualiza status
        setOrderStatus(updatedOrder.status);
        
        // Se completou, atualiza TUDO (incluindo ticket)
        if (updatedOrder.status === 'completed') {
          console.log('✅ Pedido completo! Ticket:', updatedOrder.mega_prize_ticket);
          setOrder(updatedOrder); // Atualiza pedido completo com ticket
          
          // Se Mega Prêmio ativo e ainda não tem ticket, continua polling
          if (megaPrizeConfig?.active && !updatedOrder.mega_prize_ticket) {
            console.log('⏳ Aguardando geração do ticket...');
            return; // Continua polling
          }
          
          // Para o polling quando tiver ticket OU evento não ativo
          return true; // Sinaliza para parar
        }
      } catch (err) {
        console.error('Erro ao verificar status:', err);
      }
    };
    
    // Verifica imediatamente
    checkOrderStatus();
    
    // Depois a cada 5 segundos
    const interval = setInterval(checkOrderStatus, 5000);
    
    // Para após 10 minutos
    const timeout = setTimeout(() => clearInterval(interval), 600000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [order]);

  useEffect(() => {
    loadInitialData();
    loadAdvertisements();
    loadPaymentMethods(); // Carrega métodos de pagamento

    // Evita auto-focus do navegador no campo KAKO ID ao abrir a página
    if (typeof document !== 'undefined' && document.activeElement && document.activeElement.tagName === 'INPUT') {
      document.activeElement.blur();
    }
    
    // Captura ref da URL (?ref=KAKO_ID) — APENAS sessionStorage (per-sessão).
    // Comportamento: se o cliente vier via link de afiliado, conta. Se entrar direto
    // (sem link), NÃO conta para ninguém. Ao fechar o app, sessionStorage é apagado
    // automaticamente — comportamento desejado.
    //
    // IMPORTANTE: NÃO apagamos sessionStorage ao montar sem ?ref=, pois isso
    // quebraria o tracking em PWAs (cujo launcher pode descartar query params).
    // O SW intercepta esses casos e injeta o ref via postMessage antes do React montar.
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');

    if (ref) {
      sessionStorage.setItem('ref_reseller_id', ref);
      console.log('🔗 Link de afiliado detectado e salvo (sessão):', ref);
    } else if (window.__pendingRef) {
      // SW capturou ?ref= na navegação antes do React montar
      sessionStorage.setItem('ref_reseller_id', window.__pendingRef);
      console.log('🔗 Ref recuperado via Service Worker:', window.__pendingRef);
      window.__pendingRef = null;
    } else {
      const existing = sessionStorage.getItem('ref_reseller_id');
      if (existing) {
        console.log('🔗 Ref já presente na sessão atual:', existing);
      } else {
        console.log('🚫 Acesso direto - sem afiliado');
      }
    }
  }, []);

  const loadAdvertisements = async () => {
    console.log('🎨 Carregando anúncios...');
    try {
      const response = await advertisementsAPI.listActive();
      console.log('✅ Anúncios carregados:', response.data);
      setAdvertisements(response.data);
    } catch (err) {
      console.error('❌ Erro ao carregar anúncios:', err);
    }
  };

  // Carrega métodos de pagamento disponíveis
  useEffect(() => { fetchFiscalLabels().then(setFiscalLabels); }, []);

  const loadPaymentMethods = async (customerToken = null) => {
    try {
      if (customerToken) {
        // Cliente logado: o país do cadastro define o método (PIX no Brasil,
        // método local no exterior) — o servidor manda a regra pronta.
        const r = await axios.get(`${getBackendURL()}/api/payment-methods/available-for-me`,
          { headers: { Authorization: `Bearer ${customerToken}` } });
        const metodos = r.data.methods || [];
        setAvailablePaymentMethods({
          pix: metodos.includes('pix'),
          mbway: metodos.includes('mbway'),
          mbwayMinCoins: r.data.mbway_min_coins ?? 100000,
        });
        setPaymentNotice(r.data.notice || null);
        setSelectedPaymentMethod(r.data.default || null);
        // Conta da região do euro: vitrine em euro, já com a taxa embutida
        try {
          const e = await axios.get(`${getBackendURL()}/api/payment-methods/eur-prices`,
            { headers: { Authorization: `Bearer ${customerToken}` } });
          setEurPrices(e.data?.euro ? e.data : null);
        } catch (err) {
          setEurPrices(null);
        }
        return;
      }
      const response = await paymentMethodsAPI.getAvailable();
      setEurPrices(null);
      setAvailablePaymentMethods({
        pix: response.data.pix?.enabled ?? true,
        mbway: response.data.mbway?.enabled ?? false,
        mbwayMinCoins: response.data.mbway_min_coins ?? 100000,
      });
    } catch (err) {
      console.error('❌ Erro ao carregar métodos de pagamento:', err);
    }
  };

  const loadInitialData = async () => {
    try {
      const BACKEND_URL = getBackendURL(); // Define BACKEND_URL aqui
      
      const [configRes, packagesRes, tasksRes, rankingRes] = await Promise.all([
        configAPI.get(),
        packagesAPI.list(true),
        tasksAPI.listActive().catch(() => ({ data: [] })),
        rankingAPI.getCurrent(10).catch(() => ({ data: [] }))
      ]);
      setConfig(configRes.data);
      setPackages(packagesRes.data);
      console.log('Pacotes carregados:', packagesRes.data);
      console.log('Ranking carregado:', rankingRes.data);
      console.log('Config:', configRes.data);
      if (configRes.data.tasks_active) {
        setTasks(tasksRes.data);
      }
      // Sempre carrega ranking para verificar prêmios disponíveis
      setRanking(rankingRes.data);
      if (configRes.data.ranking_active) {
        console.log('Ranking ativo! Dados:', rankingRes.data);
      }
      
      // Carrega vencedores se banner estiver ativo
      if (configRes.data.show_winners_banner) {
        const winnersRes = await applicationsAPI.getCurrentWinners();
        setWinners(winnersRes.data);
        // NÃO mostra ainda - aguarda modal de bem-vindo fechar
      }
      
      // Carrega ganhadores do dia anterior para o scroll
      const prevWinnersRes = await rankingAPI.getWinners(10).catch(() => ({ data: [] }));
      setPreviousDayWinners(prevWinnersRes.data);
      
      // Carrega anúncio de confirmação
      const paymentAdRes = await paymentAdAPI.get().catch(() => ({ data: null }));
      setPaymentConfirmationAd(paymentAdRes.data);
      
      // Carrega config MEGA PRÊMIO
      const megaPrizeRes = await megaPrizeAPI.getConfig().catch(() => ({ data: null }));
      console.log('🎰 MEGA PRÊMIO Config carregado:', megaPrizeRes.data);
      setMegaPrizeConfig(megaPrizeRes.data);
      
      // Carrega config MEU HOST PREFERIDO
      try {
        const hostEventRes = await axios.get(`${BACKEND_URL}/api/host-event/config`);
        console.log('👑 HOST Event Config carregado:', hostEventRes.data);
        console.log('👑 show_inscription:', hostEventRes.data?.show_inscription);
        console.log('👑 show_voting:', hostEventRes.data?.show_voting);
        setHostEventConfig(hostEventRes.data);
      } catch (error) {
        console.error('❌ Erro ao carregar host event config:', error);
        setHostEventConfig(null);
      }
      
      // Carrega config BATALHA DE PK
      try {
        const pkBattleRes = await axios.get(`${BACKEND_URL}/api/pk-battle/config`);
        console.log('⚔️ PK Battle Config carregado:', pkBattleRes.data);
        setPKBattleConfig(pkBattleRes.data);
      } catch (error) {
        console.error('❌ Erro ao carregar pk battle config:', error);
        setPKBattleConfig(null);
      }
      
      // Carrega config TRABALHOU GANHOU
      try {
        const hostWorkEventRes = await axios.get(`${BACKEND_URL}/api/host-work-event/config`);
        console.log('💼 Trabalhou Ganhou Config carregado:', hostWorkEventRes.data);
        setHostWorkEventConfig(hostWorkEventRes.data);
      } catch (error) {
        console.error('❌ Erro ao carregar trabalhou ganhou config:', error);
        setHostWorkEventConfig(null);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    }
  };

  const handleVerifyUser = async (overrideId = null, keepOnError = false) => {
    const safeOverride = (typeof overrideId === 'string' || typeof overrideId === 'number') ? overrideId : null;
    const idToVerify = (safeOverride || kakoId || '').toString().trim();
    if (!idToVerify) {
      setError('Por favor, insira seu KAKO ID');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await kakoAPI.verifyUser(idToVerify);
      setUserInfo(response.data.user);
      
      // Salva ID se "Lembrar" estiver marcado
      if (rememberMe) {
        localStorage.setItem('saved_kako_id', idToVerify);
      } else {
        localStorage.removeItem('saved_kako_id');
      }
      
      // Carrega progresso de tarefas se ativo
      if (config?.tasks_active) {
        await loadTaskProgress();
      }
      
      // Verifica se tem prêmios pendentes de resgate
      try {
        const prizesRes = await rankingAPI.getMyPrizes(idToVerify);
        setHasPendingPrizes(prizesRes.data.length > 0);
      } catch (err) {
        console.error('Erro ao verificar prêmios:', err);
      }
      return true;
    } catch (err) {
      if (err.response?.status === 403) {
        setBlockedMsg(err.response?.data?.detail || '');
        setUserInfo(null);
        return null;
      }
      if (!keepOnError) {
        setError(err.response?.data?.detail || 'KAKO ID inválido ou usuário não encontrado');
        setUserInfo(null);
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Cliente logado: carrega a conta e pré-preenche dados do pagador
  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${getBackendURL()}/api/customer-auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const acc = res.data;
        setCustomerAccount(acc);
        // Pagador = SEMPRE a conta verificada (dados salvos do navegador não entram)
        const wa = splitWhatsapp(acc.whatsapp, String(acc.whatsapp_dial || '55'));
        setCustomerData({
          name: (acc.name || '').toUpperCase(),
          cpf: acc.cpf || '',
          whatsapp: wa.number,
          whatsapp_country: `+${wa.dial}`,
          email: acc.email || '',
          is_foreigner: !!acc.is_foreigner,
          document: acc.fiscal_document || '',
          country: acc.country || '',
        });
        loadPaymentMethods(token);
        // Compra para si: KAKO ID = o da conta (verifica automaticamente)
        setKakoId(acc.kako_id);
        handleVerifyUser(acc.kako_id);
      } catch {
        localStorage.removeItem('customer_token');
      } finally {
        setAuthPending(false);
      }
    })();
  }, []);

  const loadTaskProgress = async () => {
    if (!kakoId) return;
    try {
      const progressRes = await tasksAPI.getProgress(kakoId);
      console.log('Task Progress carregado:', progressRes.data);
      setTaskProgress(progressRes.data);
    } catch (err) {
      console.error('Erro ao carregar progresso de tarefas:', err);
    }
  };

  const handleSelectPackage = (pkg) => {
    console.log('📦 Pacote selecionado:', pkg.package_name);
    setSelectedPackage(pkg);
    setError('');
    setShowConfirmation(true);
    console.log('✅ showConfirmation setado para TRUE');
  };

  const validateCPF = (cpf) => {
    // Remove formatação
    cpf = cpf.replace(/\D/g, '');
    
    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false; // Todos dígitos iguais
    
    // Validação dos dígitos verificadores
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cpf.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cpf.charAt(10))) return false;
    
    return true;
  };

  const formatCPF = (value) => {
    const cpf = value.replace(/\D/g, '');
    if (cpf.length <= 3) return cpf;
    if (cpf.length <= 6) return `${cpf.slice(0, 3)}.${cpf.slice(3)}`;
    if (cpf.length <= 9) return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6)}`;
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9, 11)}`;
  };

  const handleConfirmPayment = async () => {
    // Validações
    const nameParts = customerData.name.trim().split(/\s+/);
    if (nameParts.length < 2) {
      setError('Digite NOME e SOBRENOME (ex: JOÃO SILVA)');
      return;
    }
    if (customerData.name.trim().length < 6) {
      setError('Nome completo muito curto');
      return;
    }
    
    // Documento: CPF no Brasil; no exterior, o documento fiscal do país escolhido
    if (customerData.is_foreigner) {
      if (!customerData.country) {
        setError('Selecione o seu país');
        return;
      }
      const regra = docInfo(fiscalLabels, customerData.country);
      if (!docValid(fiscalLabels, customerData.country, customerData.document)) {
        setError(`${regra.label} de ${customerData.country} inválido · ${regra.hint}`);
        return;
      }
    } else {
      const cpfClean = customerData.cpf.replace(/\D/g, '');
      if (!validateCPF(cpfClean)) {
        setError('CPF inválido');
        return;
      }
    }

    // Telefone: formato do DDI informado
    const phoneDigits = (customerData.whatsapp || '').replace(/\D/g, '');
    const dial = (customerData.whatsapp_country || '+55').replace('+', '');
    if (dial === '55') {
      if (phoneDigits.length < 10 || phoneDigits.length > 11) {
        setError('WhatsApp inválido. Use DDD + número (ex: 11 99999-9999)');
        return;
      }
    } else if (phoneDigits.length < 6 || dial.length + phoneDigits.length > 15) {
      setError('Telefone inválido. Informe o número completo do seu país');
      return;
    }
    
    // Salva dados se "lembrar" estiver marcado
    if (rememberCustomerData) {
      localStorage.setItem('saved_customer_data', JSON.stringify(customerData));
    } else {
      localStorage.removeItem('saved_customer_data');
    }
    
    setShowConfirmation(false);
    setError('');
    await handleCreateOrder();
  };

  const handleCancelConfirmation = () => {
    // Volta para seleção de pacotes (mantém usuário verificado)
    setShowConfirmation(false);
    setSelectedPackage(null);
    setError('');
  };

  const handleChangeUser = () => {
    setShowConfirmation(false);
    setSelectedPackage(null);
    setError('');
    if (customerAccount) {
      // Logado: abre a troca inline no card de usuário verificado
      setSwapMode(true);
      setSwapDraft('');
      setSwapError('');
    } else {
      setUserInfo(null);
      setKakoId(rememberMe ? kakoId : '');
    }
  };

  // Troca de destinatário inline (cliente logado)
  const openSwap = () => { setSwapMode(true); setSwapDraft(''); setSwapError(''); };
  const cancelSwap = () => { setSwapMode(false); setSwapDraft(''); setSwapError(''); };

  const handleSwapVerify = async () => {
    const id = (swapDraft || '').trim();
    if (!id) { setSwapError('Digite o KAKO ID'); return; }
    setSwapError('');
    const ok = await handleVerifyUser(id, true);
    if (ok) {
      setKakoId(id);
      setSelectedPackage(null);
      setSwapMode(false);
      setSwapDraft('');
    } else {
      setSwapError('KAKO ID inválido ou usuário não encontrado');
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedPackage) return;

    setOrder(null); // Limpa pedido anterior
    setOrderStatus(null); // Limpa status anterior TAMBÉM
    setCreatingOrder(true);
    setLoading(true);
    setProcessingPayment(true); // Ativa spinner fullscreen
    setError('');
    try {
      // Ref vem APENAS da sessão atual (apagado quando cliente fecha o app)
      const ref_reseller_id = sessionStorage.getItem('ref_reseller_id');
      
      // Para estrangeiros, não envia CPF (backend usará CPF genérico na EFI)
      // Para MB WAY, envia NIF como documento
      const isMbway = selectedPaymentMethod === 'mbway';
      const cpfClean = (customerData.is_foreigner || isMbway) ? '' : customerData.cpf.replace(/\D/g, '');
      const documentId = (customerData.is_foreigner || isMbway) ? customerData.document : '';
      
      // Telefone: MB WAY usa +351, demais usa o seletor
      const phoneCountry = isMbway ? '+351' : (customerData.whatsapp_country || '+55');
      
      const response = await ordersAPI.create({
        kako_id: kakoId,
        package_id: selectedPackage.id,
        customer_name: customerData.name,
        customer_cpf: cpfClean,
        customer_document: (documentId || '').toUpperCase().replace(/[^A-Z0-9]/g, ''),
        customer_country: (customerData.is_foreigner || isMbway) ? (customerData.country || null) : null,
        is_foreigner: customerData.is_foreigner || isMbway,
        customer_whatsapp: phoneCountry + customerData.whatsapp,
        customer_email: customerData.email || (customerAccount?.email ?? null),
        count_in_ranking: countInRanking,
        ref_reseller_id: ref_reseller_id || null,
        payment_method: selectedPaymentMethod // NOVO - método de pagamento
      });
      
      const orderData = response.data;
      setOrder(orderData);
      console.log('Pedido criado:', orderData);
      
      if (ref_reseller_id) {
        console.log('✅ Pedido vinculado ao revendedor:', ref_reseller_id);
      }
    } catch (err) {
      console.error('Erro ao criar pedido:', err);
      console.error('Detalhes:', err.response?.data);
      
      const errorMessage = err.response?.data?.detail || 'Erro ao criar pedido. Por favor, tente novamente.';

      // Conta em análise por contestação de pagamento
      if (err.response?.status === 403) {
        setBlockedMsg(errorMessage);
        setShowConfirmation(false);
        return;
      }

      // Verifica se é erro de pacote indisponível
      if (errorMessage.includes('indisponível') || errorMessage.includes('Pacote indisponível')) {
        setUnavailableError(errorMessage);
        setShowConfirmation(false); // Fecha o modal de confirmação
        // Atualiza lista de pacotes
        try {
          const packagesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/packages/?active_only=true`);
          const packagesData = await packagesRes.json();
          setPackages(packagesData);
        } catch (e) {
          console.error('Erro ao atualizar pacotes:', e);
        }
      } else {
        setError(errorMessage);
        setShowConfirmation(true); // Volta pro dialog se der erro
      }
    } finally {
      setLoading(false);
      setCreatingOrder(false);
      setProcessingPayment(false); // Desativa spinner fullscreen
    }
  };

  const handleReset = () => {
    console.log('🔄 Resetando para nova compra...');
    setUserInfo(null);
    setSelectedPackage(null);
    setOrder(null); // Isso vai parar o polling (useEffect depende de order)
    setOrderStatus(null);
    setError('');
    setTaskProgress([]);
    setShowConfirmation(false);
    setCreatingOrder(false); // Garante que não está criando
    setSwapMode(false);
    setSwapDraft('');
    setSwapError('');
    if (customerAccount) {
      // Logado: volta direto para a própria conta verificada
      setKakoId(customerAccount.kako_id);
      handleVerifyUser(customerAccount.kako_id);
    } else {
      setKakoId(rememberMe ? kakoId : '');
    }
  };

  if (config?.sales_paused) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-2xl text-center">🦊 FOX SELLER</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                As vendas estão temporariamente pausadas. Por favor, tente novamente mais tarde.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${siteTheme && (siteTheme.video_horizontal_url || siteTheme.video_vertical_url) ? '' : 'bg-gradient-to-br from-orange-50 via-purple-50 to-blue-50'}`}>
      {/* Vídeo de fundo do tema ativo (admin > Tema) */}
      <ThemeBackground onTheme={setSiteTheme} />
      {siteTheme?.particles && <ThemeParticles type={siteTheme.particles} />}
      {/* Texto Scrolling de Compras Recentes - FIXO NO TOPO */}
      {config && <RecentPurchasesScroll active={config.scrolling_text_active} barColor={siteTheme?.buyers_bar_color} />}
      
      {/* Barra de Hosts Ao Vivo - Logo abaixo do scroll de compradores */}
      <LiveHostsBar frameUrl={siteTheme?.avatar_frame_url} frameFit={siteTheme?.frame_fit} barColor={siteTheme?.live_bar_color} />
      
      {/* Acesso rápido fixo: Entrar/Minha Conta + Afiliado (abaixo da barra de compradores) */}
      <TopAccessBar account={customerAccount} authPending={authPending} onBottom={setBarBottom} />

      {/* Padding: top reservado para as barras fixas; bottom para LiveHostsBar */}
      <div className="p-4 pb-32" style={{ paddingTop: barBottom + 14 }}>
        {blockedMsg && (
          <AccountBlockedNotice
            message={blockedMsg}
            supportLink={config?.support_button_link}
            onClose={() => setBlockedMsg('')}
          />
        )}
        <div className="max-w-3xl lg:max-w-lg xl:max-w-xl mx-auto py-2">
        {/* Header removido a pedido do usuário — anúncios sobem */}

        {!order ? (
          <div className="space-y-6">
            {/* Conteúdo Principal Centralizado */}
            <div className="space-y-6">
              {/* Verificação de Usuário */}
              {!userInfo ? (
                <>
                  {/* Carrossel - Acima da Verificação */}
                  {advertisements.length > 0 && (
                    <AdvertisementCarousel advertisements={advertisements} />
                  )}

                  {customerAccount ? (
                    /* Logado: nunca mostra o card de inserir ID — verifica a conta automaticamente */
                    <Card data-testid="account-loading-card" className="border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50 to-purple-50 shadow-xl shadow-purple-200/40">
                      <CardContent className="py-10">
                        {error ? (
                          <div className="space-y-3 text-center">
                            <Alert variant="destructive" data-testid="account-verify-error">
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription>{error}</AlertDescription>
                            </Alert>
                            <Button
                              data-testid="account-retry-button"
                              onClick={() => { setError(''); handleVerifyUser(customerAccount.kako_id); }}
                              className="bg-gradient-to-br from-orange-400 to-orange-600 text-white"
                            >
                              Tentar novamente
                            </Button>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                            <p className="text-sm text-purple-700">
                              Verificando sua conta{customerAccount.nickname ? ` · ${customerAccount.nickname}` : ''}...
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                  <Card data-testid="kako-id-card" className="border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50 to-purple-50 shadow-xl shadow-purple-200/40 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-purple-900 flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white text-sm font-bold shadow-md shadow-orange-300/60">1</span>
                        Insira seu KAKO ID
                      </CardTitle>
                      <CardDescription className="text-purple-600/80">Digite seu ID para verificação</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-2">
                        <Input
                          data-testid="kako-id-input"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={8}
                          placeholder="Digite seu KAKO ID"
                          value={kakoId}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 8) {
                              setKakoId(value);
                            }
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key) && e.key !== 'Enter') {
                              e.preventDefault();
                            }
                            if (e.key === 'Enter') {
                              handleVerifyUser();
                            }
                          }}
                          autoComplete="off"
                          className="text-lg bg-white text-gray-900 placeholder:text-gray-400 border-purple-200 focus-visible:ring-orange-400"
                        />
                        <Button
                          data-testid="verify-kako-button"
                          onClick={() => handleVerifyUser()}
                          disabled={loading}
                          size="lg"
                          className="bg-gradient-to-br from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 shadow-lg shadow-orange-300/50 text-white"
                        >
                          {loading ? <Loader2 className="animate-spin" /> : 'Verificar'}
                        </Button>
                      </div>
                      {/* Checkbox Lembrar ID */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="remember-id"
                          checked={rememberMe}
                          onCheckedChange={setRememberMe}
                          className="border-purple-300 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                        />
                        <Label 
                          htmlFor="remember-id" 
                          className="text-sm text-purple-700 cursor-pointer"
                        >
                          Lembrar meu ID para próxima visita
                        </Label>
                      </div>
                      {error && (
                        <Alert variant="destructive" data-testid="error-alert">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                  )}
                </>
              ) : (
                <>
                  {/* Carrossel - Mantido após validação */}
                  {advertisements.length > 0 && (
                    <AdvertisementCarousel advertisements={advertisements} />
                  )}

                  {/* Informações do Usuário */}
                  <Card data-testid="user-info-card" className="border-green-200 bg-green-50">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {userInfo.avatar && (
                            <FramedAvatar
                              src={userInfo.avatar}
                              frameUrl={siteTheme?.avatar_frame_url}
                              fit={siteTheme?.frame_fit}
                              size={104}
                            />
                          )}
                          <div>
                            <p className="text-sm text-gray-600">
                              {customerAccount && kakoId !== customerAccount.kako_id ? '🎁 Presente para' : 'Usuário verificado'}
                            </p>
                            <p className="text-2xl font-bold text-gray-800">{userInfo.nickname}</p>
                            <p className="text-sm text-gray-500">ID: {kakoId}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="text-green-500 w-8 h-8" />
                          <Button
                            data-testid="change-user-button-top"
                            onClick={() => (customerAccount ? (swapMode ? cancelSwap() : openSwap()) : handleReset())}
                            variant="outline"
                            size="sm"
                          >
                            {customerAccount && swapMode ? 'Cancelar' : 'Trocar'}
                          </Button>
                        </div>
                      </div>

                      {/* Presente: comprando para outro ID (logado) */}
                      {customerAccount && kakoId !== customerAccount.kako_id && !swapMode && (
                        <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50/70 p-2.5" data-testid="gift-recipient-banner">
                          <p className="text-[11px] text-orange-700 leading-tight">
                            As moedas vão para este ID. O pagamento usa os dados da sua conta ({customerAccount.kako_id}).
                          </p>
                        </div>
                      )}

                      {/* Troca inline de destinatário (logado) */}
                      {customerAccount && swapMode && (
                        <div className="mt-3 space-y-2 rounded-lg border border-purple-200 bg-white/80 p-3" data-testid="swap-id-panel">
                          <p className="text-xs font-medium text-purple-800">
                            Digite o KAKO ID de quem vai receber as moedas:
                          </p>
                          <div className="flex gap-2">
                            <Input
                              data-testid="swap-id-input"
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              maxLength={8}
                              placeholder="KAKO ID"
                              value={swapDraft}
                              autoFocus
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                if (value.length <= 8) setSwapDraft(value);
                              }}
                              onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key) && e.key !== 'Enter') e.preventDefault();
                                if (e.key === 'Enter') handleSwapVerify();
                              }}
                              autoComplete="off"
                              className="bg-white text-gray-900 border-purple-200 focus-visible:ring-orange-400"
                            />
                            <Button
                              data-testid="swap-verify-button"
                              onClick={handleSwapVerify}
                              disabled={loading}
                              className="bg-gradient-to-br from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white"
                            >
                              {loading ? <Loader2 className="animate-spin" /> : 'Verificar'}
                            </Button>
                          </div>
                          {swapError && (
                            <Alert variant="destructive" data-testid="swap-error-alert">
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription>{swapError}</AlertDescription>
                            </Alert>
                          )}
                          <p className="text-[11px] text-gray-500 leading-tight">
                            O pagamento continua com os dados da sua conta ({customerAccount.kako_id}).
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Tabs: SEMPRE MOSTRA (pelo menos Pacotes) */}
                  <Tabs defaultValue="packages" className="w-full">
                    <TabsList className={`grid w-full ${
                      // Conta quantas abas terão
                      (() => {
                        let count = 1; // Pacotes sempre
                        if (config?.tasks_active && taskProgress.length > 0) count++;
                        if (config?.ranking_active || config?.ranking_show_prizes) count++;
                        if (megaPrizeConfig?.active) count++;
                        if (hostEventConfig?.show_inscription || hostEventConfig?.show_voting) count++;
                        if (pkBattleConfig?.show_inscription_tab || pkBattleConfig?.show_rounds_tab || pkBattleConfig?.show_winners_tab) count++;
                        
                        
                        return `grid-cols-${count}`;
                      })()
                    }`}>
                        <TabsTrigger 
                          value="packages" 
                          data-testid="tab-packages-client" 
                          className="text-xs sm:text-sm bg-orange-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white"
                          onClick={() => {
                            // Recarrega pacotes, MEGA PRÊMIO E tarefas
                            packagesAPI.list(true).then(res => setPackages(res.data));
                            megaPrizeAPI.getConfig().then(res => setMegaPrizeConfig(res.data)).catch(() => {});
                            if (kakoId) {
                              tasksAPI.getProgress(kakoId).then(res => setTaskProgress(res.data)).catch(() => {});
                            }
                          }}
                        >
                          <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">Pacotes</span>
                          <span className="sm:hidden">Pac</span>
                        </TabsTrigger>
                        {config?.tasks_active && taskProgress.length > 0 && (
                          <TabsTrigger 
                            value="tasks" 
                            data-testid="tab-tasks-client" 
                            className={`text-xs sm:text-sm bg-purple-200 data-[state=active]:bg-purple-600 data-[state=active]:text-white ${
                              !visitedTabs.tasks ? 'neon-purple' : ''
                            }`}
                            onClick={() => markTabAsVisited('tasks')}
                            onMouseDown={() => markTabAsVisited('tasks')}
                          >
                            <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">TAREFAS VIP</span>
                            <span className="sm:hidden">VIP</span>
                          </TabsTrigger>
                        )}
                        {(config?.ranking_active || config?.ranking_show_prizes) && (
                          <TabsTrigger 
                            value="ranking" 
                            data-testid="tab-ranking-client" 
                            className={`text-xs sm:text-sm bg-blue-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-blue-600 data-[state=active]:text-white ${
                              !visitedTabs.ranking ? 'neon-blue' : ''
                            }`}
                            onClick={() => markTabAsVisited('ranking')}
                            onMouseDown={() => markTabAsVisited('ranking')}
                          >
                            <Trophy className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">{config?.ranking_active ? 'Ranking' : 'Prêmios'}</span>
                            <span className="sm:hidden">Rank</span>
                            {hasPendingPrizes && <Badge className="ml-1 bg-yellow-600 text-xs">!</Badge>}
                          </TabsTrigger>
                        )}
                        {megaPrizeConfig?.active && (
                          <TabsTrigger 
                            value="megaprize" 
                            className={`text-xs sm:text-sm bg-yellow-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-500 data-[state=active]:to-orange-500 data-[state=active]:text-white ${
                              !visitedTabs.megaprize ? 'neon-yellow' : ''
                            }`}
                            onClick={() => markTabAsVisited('megaprize')}
                            onMouseDown={() => markTabAsVisited('megaprize')}
                          >
                            <Trophy className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">MEGA PRÊMIO</span>
                            <span className="sm:hidden">Mega</span>
                          </TabsTrigger>
                        )}
                        
                        
                        {/* MEU HOST PREFERIDO - Neon VERDE */}
                        {(hostEventConfig?.show_inscription || hostEventConfig?.show_voting) && (
                          <TabsTrigger 
                            value="hostevent" 
                            className={`text-xs sm:text-sm bg-pink-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-500 data-[state=active]:text-white ${
                              !visitedTabs.hostevent ? 'neon-pink' : ''
                            }`}
                            onClick={() => markTabAsVisited('hostevent')}
                            onMouseDown={() => markTabAsVisited('hostevent')}
                          >
                            <Crown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">MEU HOST PREFERIDO</span>
                            <span className="sm:hidden">Host</span>
                          </TabsTrigger>
                        )}
                        
                        {/* BATALHA DE PK - Neon VERMELHO */}
                        {(pkBattleConfig?.show_inscription_tab || pkBattleConfig?.show_rounds_tab || pkBattleConfig?.show_winners_tab) && (
                          <TabsTrigger 
                            value="pkbattle" 
                            className={`text-xs sm:text-sm bg-red-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-orange-500 data-[state=active]:text-white ${
                              !visitedTabs.pkbattle ? 'neon-red' : ''
                            }`}
                            onClick={() => markTabAsVisited('pkbattle')}
                            onMouseDown={() => markTabAsVisited('pkbattle')}
                          >
                            <Swords className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">BATALHA DE PK</span>
                            <span className="sm:hidden">PK</span>
                          </TabsTrigger>
                        )}
                        {console.log('🎯 Verificações:', {
                          'tasks_active': config?.tasks_active,
                          'taskProgress': taskProgress.length,
                          'ranking_active': config?.ranking_active || hasPendingPrizes,
                          'megaPrize_active': megaPrizeConfig?.active,
                          'megaPrizeConfig': megaPrizeConfig,
                          'hostEvent_show_inscription': hostEventConfig?.show_inscription,
                          'hostEvent_show_voting': hostEventConfig?.show_voting,
                          'hostEventConfig': hostEventConfig
                        })}
                      </TabsList>

                      {/* Scroll de Ganhadores - Aparece se Ranking OU Prêmios ativo */}
                      {(config?.ranking_active || config?.ranking_show_prizes) && previousDayWinners.length > 0 && (
                        <div className="mt-3 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-300 rounded p-2 overflow-hidden">
                          <div className="overflow-hidden">
                            <div className="flex animate-scroll gap-6 whitespace-nowrap">
                              {previousDayWinners.concat(previousDayWinners).map((winner, idx) => (
                                <div key={`${winner.kako_id}-${idx}`} className="inline-flex items-center gap-2 text-xs">
                                  <span className="font-bold">{winner.position}º</span>
                                  {winner.kako_avatar && (
                                    <img src={winner.kako_avatar} alt="" className="w-6 h-6 rounded-full border border-blue-300" />
                                  )}
                                  <span className="font-medium">{winner.kako_nickname}</span>
                                  <span className="text-blue-600 font-bold">+{winner.prize_coins?.toLocaleString() || 0} 🎁</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <EventBannerCarousel />
                      <PackagesGrid {...{ handleSelectPackage, packages, selectedPackage, eurPrices }} />

                      {config?.tasks_active && taskProgress.length > 0 && (
                        <TabsContent value="tasks" className="mt-4">
                          <ClientTasks 
                            kakoId={kakoId} 
                            taskProgress={taskProgress}
                            onProgressUpdate={loadTaskProgress}
                          />
                        </TabsContent>
                      )}

                      {(config?.ranking_active || config?.ranking_show_prizes) && (
                        <TabsContent value="ranking" className="mt-4">
                          <ClientRanking 
                            kakoId={kakoId}
                            userInfo={userInfo}
                            initialRanking={ranking}
                            showCurrentRanking={config?.ranking_active}
                            showPrizes={config?.ranking_show_prizes}
                            hasPendingPrizes={hasPendingPrizes}
                          />
                        </TabsContent>
                      )}

                      <TabsContent value="megaprize" className="mt-4">
                        <ClientMegaPrize kakoId={kakoId} />
                      </TabsContent>
                      
                      {/* Aba MEU HOST PREFERIDO */}
                      {(hostEventConfig?.show_inscription || hostEventConfig?.show_voting) && (
                        <TabsContent value="hostevent" className="mt-4">
                          <ClientHostEvent kakoId={kakoId} />
                        </TabsContent>
                      )}
                      
                      {/* Aba BATALHA DE PK */}
                      {(pkBattleConfig?.show_inscription_tab || pkBattleConfig?.show_rounds_tab || pkBattleConfig?.show_winners_tab || pkBattleConfig?.show_rules_tab) && (
                        <TabsContent value="pkbattle" className="mt-4">
                          <ClientPKBattle 
                            userKakoId={kakoId}
                            userNickname={userInfo?.nickname}
                            userAvatar={userInfo?.avatar}
                          />
                        </TabsContent>
                      )}


                    </Tabs>

                  
                </>
              )}
            </div>
          </div>
        ) : (
          /* Mostra Loading ou Pedido */
          <>
          {creatingOrder ? (
          /* Spinner de Carregamento - PRIORIDADE */
          <Card className="max-w-2xl mx-auto">
            <CardContent className="py-20">
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="relative flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_8f198eda-3cd9-45f6-a741-5b80db2d25c7/artifacts/wkn4ynag_incone.png"
                    alt="Carregando"
                    className="w-16 h-16 animate-pulse"
                  />
                  <div className="absolute w-24 h-24 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-gray-600">Gerando QR Code PIX...</p>
              </div>
            </CardContent>
          </Card>
        ) : order ? (
          <OrderPaymentView {...{ creatingOrder, handleReset, hostEventConfig, order, orderStatus, paymentConfirmationAd, timeRemaining, userInfo }} />
        ) : null}
        </>
        )}
      </div>

      {/* Modal Badge ADM */}
      <Dialog open={showAdmBadge} onOpenChange={setShowAdmBadge}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_kako-payments/artifacts/obqys81y_IMG_7276.PNG"
            alt="Certificado ADM"
            className="w-full h-auto"
          />
        </DialogContent>
      </Dialog>

      {/* Modal Badge Seller */}
      <Dialog open={showSellerBadge} onOpenChange={setShowSellerBadge}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_kako-payments/artifacts/kkpaekww_IMG_7275.PNG"
            alt="Certificado Seller"
            className="w-full h-auto"
          />
        </DialogContent>
      </Dialog>

      {/* Botão Flutuante de Suporte - Configurável pelo Admin */}
      <SupportButton />

      {/* Audio Player */}
      <AudioPlayer onWelcomeClosed={() => setWelcomeClosed(true)} />

      {/* Dialog de Confirmação de Pagamento - SEM CAIXA AMARELA */}
      <CheckoutDialog {...{ availablePaymentMethods, config, countInRanking, customerAccount, customerData, error, formatCPF, handleCancelConfirmation, handleChangeUser, handleConfirmPayment, kakoId, loading, paymentNotice, rememberCustomerData, selectedPackage, selectedPaymentMethod, setCountInRanking, setCustomerData, setRememberCustomerData, setSelectedPaymentMethod, showConfirmation, userInfo, validateCPF }} />

      {/* Banner TOP 3 Vencedores */}
      <WinnersBanner {...{ setShowWinnersBanner, showWinnersBanner, winners }} />
      </div>

      {/* Spinner Fullscreen - Processando Pagamento */}
      {processingPayment && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_8f198eda-3cd9-45f6-a741-5b80db2d25c7/artifacts/wkn4ynag_incone.png"
                alt="Processando"
                className="w-24 h-24 animate-pulse"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
            <p className="text-white text-lg font-semibold animate-pulse">
              Processando seu pedido...
            </p>
          </div>
        </div>
      )}

      {/* Modal de Pacote Indisponível */}
      {unavailableError && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Ops!</h3>
                <p className="text-gray-600">{unavailableError}</p>
              </div>
              <Button
                onClick={() => {
                  setUnavailableError(null);
                  setSelectedPackage(null);
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3"
              >
                Entendi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;